import { ApolloLink, Operation, FetchResult, Observable, Observer } from '@apollo/client';
import { print } from 'graphql';
import axios, { AxiosInstance, AxiosError } from 'axios';
import AuthInterceptor from '../apiClient/authInterceptor';

interface AuthInterceptorAxiosInstance extends AxiosInstance {
  authInterceptor?: AuthInterceptor;
}

interface GraphQLErrorResponse {
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: Array<string | number>;
    extensions?: Record<string, unknown>;
  }>;
}

export class AxiosLink extends ApolloLink {
  private axiosInstance: AuthInterceptorAxiosInstance;

  private static MAX_RETRIES = 3;

  constructor(axiosInstance: AxiosInstance) {
    super();
    this.axiosInstance = axiosInstance as AuthInterceptorAxiosInstance;
  }

  request(operation: Operation): Observable<FetchResult> {
    return new Observable((observer) => {
      const source = axios.CancelToken.source();
      const { headers } = operation.getContext();
      const query = print(operation.query);

      const sendRequest = (accessToken?: string) => {
        if (accessToken) {
          operation.setContext(({ headers: existingHeaders = {} }) => ({
            headers: { ...existingHeaders, Authorization: `Bearer ${accessToken}` },
          }));
        }

        this.axiosInstance
          .post(
            '/graphql',
            {
              query,
              variables: operation.variables || {},
              operationName: operation.operationName || '',
            },
            {
              headers: { ...headers, 'Content-Type': 'application/json' },
              cancelToken: source.token,
            }
          )
          .then((response) => {
            observer.next(response.data);
            observer.complete();
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              observer.complete();
              return;
            }

            const axiosError = err as AxiosError;

            if (axiosError && axiosError.response) {
              if (axiosError.response.status === 401) {
                this.handle401Error(operation, observer);
              } else {
                const errorData = axiosError.response.data as GraphQLErrorResponse;
                observer.error({
                  graphQLErrors: errorData?.errors || [],
                  networkError: new Error(`Server error: ${axiosError.response.status}`),
                });
              }
            } else if (axiosError && axiosError.request) {
              observer.error({ networkError: new Error('No server response') });
            } else {
              observer.error({ networkError: err instanceof Error ? err : new Error(String(err)) });
            }
          });
      };

      sendRequest();
      return () => source.cancel();
    });
  }

  private handle401Error(operation: Operation, observer: Observer<FetchResult>) {
    // Get retry count from context or default to 0
    const context = operation.getContext();
    const retryCount = context.retryCount || 0;

    if (retryCount >= AxiosLink.MAX_RETRIES && observer.error) {
      observer.error({
        networkError: new Error(`Maximum retry attempts (${AxiosLink.MAX_RETRIES}) exceeded`),
      });
      return;
    }

    // Set retry count for next attempt
    operation.setContext({
      ...context,
      retryCount: retryCount + 1,
    });

    const authInterceptor = this.getAuthInterceptor();
    if (!authInterceptor && observer.error) {
      observer.error({
        networkError: new Error('AuthInterceptor not found'),
      });
      return;
    }

    new Promise<string>((resolve, reject) => {
      if (authInterceptor) {
        authInterceptor.queueFailedRequest(resolve, reject);
      } else {
        reject(new Error('AuthInterceptor not found'));
      }
    })
      .then((newToken) => {
        // Retry the request with the new token
        operation.setContext(({ headers: existingHeaders = {} }) => ({
          headers: { ...existingHeaders, Authorization: `Bearer ${newToken}` },
        }));
        this.request(operation).subscribe(observer);
      })
      .catch((err) => {
        // Notify the observer of the token refresh failure
        if (observer.error) {
          observer.error({
            networkError: err instanceof Error ? err : new Error('Token refresh failed'),
          });
        }
        // Clear tokens and notify the user
        if (authInterceptor) {
          authInterceptor.clearTokens();
        }
      });
  }

  private getAuthInterceptor(): AuthInterceptor | undefined {
    return this.axiosInstance.authInterceptor;
  }
}
